import { ReactElement } from 'react';
import { useTranslation } from 'next-i18next';
import Button from '@Hotels-at-Home/hah-fe-core/dist/components/Button';
import { Link } from '@Hotels-at-Home/hah-fe-core/dist/components';
import BmsmChart from '~/ui/components/common/Banner/BmsmChart';
import { IGetPromotionResponse } from '@Hotels-at-Home/hah-fe-core/dist/types';
import { COOKIE_CURRENCY_NAME } from '@Hotels-at-Home/hah-fe-core/dist/constants';
import { getCookie } from '@Hotels-at-Home/hah-fe-core/dist/utils/cookies';

interface IProps {
  promotion: IGetPromotionResponse;
}

const HeroSection = ({ promotion }: IProps): ReactElement => {
  const { t } = useTranslation('', { keyPrefix: '$pages.$home.$heroSection' });
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
  const { t: tCommon } = useTranslation('', { keyPrefix: '$common' });
  const { t: tLink } = useTranslation('', { keyPrefix: '$local.$homePageLinks.$hero' });

  const domain = process.env.NEXT_PUBLIC_SITE_URL;
  const isDevelopment =
    process.env.NODE_ENV === 'development' ||
    domain?.includes('localhost') ||
    domain?.includes('dev-eu') ||
    domain?.includes('staging-eu');

  const { promo_end: promoEnd } = promotion;

  const endDate = new Date(promoEnd);
  const endDay = endDate.toLocaleDateString('en-US', { weekday: 'long' });

  const currency = getCookie(COOKIE_CURRENCY_NAME) as string;

  const countdownIsActive = promotion.promo_days_left <= 3;

  const isBmsmHero = promotion.promo_is_active && promotion.promo_type === 'bmsm';

  // The page must render a single h1 (SEO) that stays in the accessibility tree at every
  // breakpoint. Desktop and mobile layouts are both in the DOM with one hidden per
  // breakpoint, so the h1 lives outside both as screen-reader-only text and the
  // visible headlines are not h1s.
  const pageTitle = `${t('bring')} ${t('wonderful')} ${t('home')}`;

  return (
    <section className="base-section hero-section main-hp" id="HP_hero_bed">
      <h1 className="sr-only">{pageTitle}</h1>
      <Link href={tLink('bed')} noTabbing>
        <div className="desktop-viewport">
          {/* TODO: create a component for normal hero / bmsm hero? AFTER BMSM PROMO LAUNCHS */}
          {isBmsmHero ? (
            <>
              <div className="bmsm-hero-wrapper">
                <div className="img-wrap">
                  <picture>
                    <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                    <source srcSet="/images/homePage/heroSection/bed-background.webp" />
                    <img
                      src="/images/homePage/heroSection/bed-background.webp"
                      alt={t('marriottBed')}
                      width="1840"
                      height="660"
                      loading="eager"
                      fetchPriority="high"
                    />
                  </picture>
                  <div className="cinema-gif" id="hero-cinema-gif">
                    <picture>
                      <source srcSet="/images/homePage/fill.gif" media="(max-width: 801px)" />
                      <img
                        srcSet="/images/homePage/heroSection/bed-overlay.webp"
                        alt={tCommon('productName', { productName: t('marriottBed') })}
                        loading="lazy"
                        decoding="async"
                        fetchPriority="low"
                        width="1840"
                        height="660"
                      />
                    </picture>
                  </div>
                </div>
                <BmsmChart
                  promotion={promotion}
                  daysLeft={countdownIsActive ? promotion.promo_days_left : null}
                  ends={endDay}
                  currency={currency}
                />
              </div>
              {isDevelopment &&
                [2, 1, 0].map(i => (
                  <div className="bmsm-hero-wrapper" key={i}>
                    <div className="img-wrap">
                      <picture>
                        <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                        <source srcSet="/images/homePage/heroSection/bed-background.webp" />
                        <img
                          src="/images/homePage/heroSection/bed-background.webp"
                          alt={t('marriottBed')}
                          width="1920"
                          height="640"
                        />
                      </picture>
                      <div className="cinema-gif" id="hero-cinema-gif">
                        <picture>
                          <source srcSet="/images/homePage/fill.gif" media="(max-width: 801px)" />
                          <img
                            srcSet="/images/homePage/heroSection/bed-overlay.webp"
                            alt={tCommon('productName', { productName: t('marriottBed') })}
                            loading="lazy"
                            width="1920"
                            height="640"
                          />
                        </picture>
                      </div>
                    </div>
                    <BmsmChart
                      promotion={promotion}
                      key={i}
                      daysLeft={i}
                      ends={endDay}
                      currency={currency}
                    />
                  </div>
                ))}
            </>
          ) : (
            <>
              <div className="img-wrap">
                <picture>
                  <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                  <source srcSet="/images/homePage/heroSection/bed-background.webp" />
                  <img
                    src="/images/homePage/heroSection/bed-background.webp"
                    alt={t('marriottBed')}
                    width="1840"
                    height="660"
                    loading="eager"
                    fetchPriority="high"
                  />
                </picture>
                <div className="cinema-gif" id="hero-cinema-gif">
                  <picture>
                    <source srcSet="/images/homePage/fill.gif" media="(max-width: 801px)" />
                    <img
                      srcSet="/images/homePage/heroSection/bed-overlay.webp"
                      alt={tCommon('productName', { productName: t('marriottBed') })}
                      loading="lazy"
                      decoding="async"
                      fetchPriority="low"
                      width="1840"
                      height="660"
                    />
                  </picture>
                </div>
              </div>
              <div className="stack">
                {/* Visual copy of the sr-only h1 above, hidden from AT to avoid a duplicate */}
                <div className="main-section-title font-main-heading-big" aria-hidden="true">
                  {t('bring')}&nbsp;
                  <span className="accent-bar">
                    {t('wonderful')}&nbsp;
                    <em data-gsap="grow" data-gsap-delay="0.05" data-gsap-duration="0.8" />
                  </span>
                  {t('home')}&nbsp;
                </div>
                <p className="txt">{t('sink')}</p>
                <Button ariaLabel={`${tButton('shopNow')}: ${t('marriottBed')}`}>
                  {tButton('shopNow')}
                </Button>
              </div>
            </>
          )}
        </div>
      </Link>
      <div className="hp-mobile-viewport">
        <a href={tLink('bed')} id="HP_hero_bed-mob" aria-label={t('mobMarriottBed')}>
          <picture>
            <source srcSet="/images/homePage/fill.gif" media="(min-width: 801px)" />
            <source srcSet="/images/homePage/heroSection/the-marriott-bed-mob.webp" />
            <img
              src="/images/homePage/heroSection/the-marriott-bed-mob.webp"
              alt={tCommon('productName', { productName: t('mobMarriottBed') })}
              width="800"
              height="394"
              loading="eager"
              fetchPriority="high"
            />
          </picture>
          <h2 className="mob-section mob-section-full-width txt">{t('mobMarriottBed')}</h2>
          {isBmsmHero ? (
            <>
              <BmsmChart
                promotion={promotion}
                daysLeft={countdownIsActive ? promotion.promo_days_left : null}
                ends={endDay}
                currency={currency}
              />
              {isDevelopment &&
                [2, 1, 0].map(i => (
                  <BmsmChart
                    promotion={promotion}
                    key={i}
                    daysLeft={i}
                    ends={endDay}
                    currency={currency}
                  />
                ))}
            </>
          ) : null}
        </a>
      </div>
    </section>
  );
};

export default HeroSection;
